import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, alpha, Box, Button, Chip, CircularProgress, InputAdornment, MenuItem, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useFuzzScanTarget } from 'context/FuzzScanTargetContext';
import { apiGetAll } from 'api/service';
import { importSbomFile, matchSbomVulnerabilities } from 'api/features';
import { downloadAuthed } from 'api/download';
import { GREEN, MONO, ago } from './scanUi';
import { clean, ecoBars, flawed, licenseBars, pct, search, summary } from './sbomLogic';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const fail = (e, fb) => e?.response?.data?.detail || e?.message || fb;
const Pill = ({ color, children }) => <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, px: 1.3, py: 0.5, borderRadius: 99, bgcolor: alpha(color, 0.16), color, whiteSpace: 'nowrap' }}>{children}</Typography>;
const SHOWN = 30;

const Bar = ({ label, n, total, color }) => (
  <Stack direction="row" alignItems="center" gap={1.5} sx={{ py: 0.7 }}>
    <Typography variant="body2" sx={{ width: 190 }} noWrap>{label}</Typography>
    <Box sx={{ flex: 1, height: 9, borderRadius: 5, bgcolor: 'divider' }}><Box sx={{ height: '100%', width: `${pct(n, total)}%`, borderRadius: 5, bgcolor: color }} /></Box>
    <Typography variant="body2" sx={{ width: 40, textAlign: 'right', fontFamily: MONO }} color="text.secondary">{n}</Typography>
  </Stack>
);

export default function SbomPage() {
  const { currentTargetId, targets, setCurrentTargetId } = useFuzzScanTarget();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [q, setQ] = useState('');
  const [shown, setShown] = useState(SHOWN);
  const [toast, setToast] = useState(null);
  const fileRef = useRef();

  const load = useCallback(async () => {
    if (!currentTargetId) { setRows([]); return; }
    setLoading(true);
    try { setRows(await apiGetAll(`/api/sbom/?user_target=${currentTargetId}`)); } catch (e) { setToast({ severity: 'error', message: fail(e, 'Could not load the software list.') }); }
    setLoading(false);
  }, [currentTargetId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => setShown(SHOWN), [q, currentTargetId]);

  const importFile = async (file) => {
    if (!file) return;
    setBusy('import');
    try {
      const r = await importSbomFile(currentTargetId, file);
      setToast({ severity: 'success', message: `${r.added} added, ${r.updated} already there. Now check them for known flaws.` });
      await load();
    } catch (e) { setToast({ severity: 'error', message: fail(e, 'Could not read that file.') }); }
    setBusy('');
  };
  const check = async () => {
    setBusy('check');
    try {
      await matchSbomVulnerabilities({ user_target: currentTargetId, include_nvd: true });
      setToast({ severity: 'success', message: 'Checking started. Results appear here in a minute or two.' });
      setTimeout(load, 6000);
    } catch (e) { setToast({ severity: 'error', message: fail(e, 'Could not start the check.') }); }
    setBusy('');
  };
  const download = async () => {
    try { await downloadAuthed(`/api/sbom/export/?user_target=${currentTargetId}`); } catch (e) { setToast({ severity: 'error', message: fail(e, 'The download failed.') }); }
  };

  const sum = useMemo(() => summary(rows), [rows]);
  const bad = useMemo(() => flawed(rows), [rows]);
  const rest = useMemo(() => search(clean(rows), q), [rows, q]);
  const stat = (label, value, sub, color) => (
    <Box sx={{ ...panel, p: 2.2, flex: '1 1 190px' }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 700, fontFamily: MONO, color }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{sub}</Typography>
    </Box>
  );

  return (
    <Stack spacing={2.5} className="fuzzscan-page" sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1.5}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Software list (SBOM)</Typography>
          <Typography color="text.secondary" variant="body2">The packages with known flaws first, then everything else</Typography>
        </Box>
        {currentTargetId && (
          <Stack direction="row" gap={1} flexWrap="wrap">
            <input ref={fileRef} type="file" hidden accept=".json,.txt,application/json,text/plain" onChange={(e) => { importFile(e.target.files[0]); e.target.value = ''; }} />
            <Button variant="outlined" color="inherit" disabled={busy === 'import'} onClick={() => fileRef.current.click()}>Import a list</Button>
            <Button variant="outlined" color="inherit" disabled={!rows.length} onClick={download}>Download (CycloneDX)</Button>
            <Button variant="contained" disabled={busy === 'check' || !rows.length} onClick={check} sx={{ fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } }}>Check for known flaws</Button>
          </Stack>
        )}
      </Stack>

      {(targets || []).length > 1 && (
        <TextField select size="small" label="Workspace" value={currentTargetId || ''} onChange={(e) => setCurrentTargetId(e.target.value)} sx={{ maxWidth: 320 }}>
          {targets.map((t) => <MenuItem key={t.id} value={t.id}>{t.name || t.target?.value}</MenuItem>)}
        </TextField>
      )}
      {!currentTargetId && <Alert severity="info">Choose a target at the top of the page first.</Alert>}
      {loading && <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress sx={{ color: GREEN }} /></Box>}

      {currentTargetId && !loading && !rows.length && (
        <Box sx={{ ...panel, p: 4, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>No software list yet</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>Import a CycloneDX file, or a list with one package per line like <code>name==1.2.3</code>. We then check each package against the public OSV and NVD databases.</Typography>
          <Button variant="contained" onClick={() => fileRef.current.click()} sx={{ mt: 2, fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } }}>Import a list</Button>
        </Box>
      )}

      {rows.length > 0 && (
        <>
          <Stack direction="row" gap={2} flexWrap="wrap">
            {stat('Packages', sum.total, `in ${sum.ecosystems} type${sum.ecosystems === 1 ? '' : 's'}`)}
            {stat('With known flaws', sum.flawedCount, `${sum.cves} CVE${sum.cves === 1 ? '' : 's'} in total`, sum.flawedCount ? '#f87171' : GREEN)}
            {stat('Licence to review', sum.review, 'copyleft, restricted or unknown', sum.review ? '#fcd34d' : undefined)}
            {stat('Last checked', sum.lastChecked ? ago(sum.lastChecked) : 'Never', sum.lastChecked ? '' : 'press Check for known flaws')}
          </Stack>

          {bad.length > 0 && (
            <Box sx={{ ...panel, borderColor: alpha('#ef4444', 0.4), overflow: 'hidden' }}>
              <Typography sx={{ p: 2.2, fontWeight: 700 }}>Fix these first</Typography>
              {bad.map((p, i) => (
                <Stack key={p.id} direction="row" alignItems="center" gap={2} flexWrap="wrap" sx={{ px: 2.8, py: 1.6, borderTop: 1, borderColor: 'divider' }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 13, bgcolor: alpha('#ef4444', 0.14), color: '#f87171' }}>{i + 1}</Box>
                  <Box sx={{ flex: '2 1 220px', minWidth: 0 }}>
                    <Typography sx={{ fontFamily: MONO, fontWeight: 700 }}>{p.name} <span style={{ opacity: 0.6, fontWeight: 400 }}>{p.version}</span></Typography>
                    <Typography variant="caption" color="text.secondary">{p.ecosystem} · {p.direct ? 'Direct' : 'Transitive'}{p.license ? ` · ${p.license}` : ''}</Typography>
                  </Box>
                  <Stack direction="row" gap={0.6} flexWrap="wrap" sx={{ flex: '2 1 240px' }}>
                    {(p.cve_ids || []).slice(0, 4).map((c) => <Pill key={c} color="#ef4444">{c}</Pill>)}
                    {!(p.cve_ids || []).length && <Pill color="#ef4444">{p.vulns} known flaw{p.vulns === 1 ? '' : 's'}</Pill>}
                    {(p.cve_ids || []).length > 4 && <Typography variant="caption" color="text.secondary">+{p.cve_ids.length - 4} more</Typography>}
                  </Stack>
                </Stack>
              ))}
            </Box>
          )}

          <Stack direction="row" gap={2} flexWrap="wrap" alignItems="flex-start">
            <Box sx={{ ...panel, p: 2.6, flex: '1 1 380px' }}>
              <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Licences</Typography>
              {licenseBars(rows).map((b) => <Bar key={b.id} label={b.label} n={b.n} total={rows.length} color={b.color} />)}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Copyleft licences may require you to share your own code if you ship the package.</Typography>
            </Box>
            <Box sx={{ ...panel, p: 2.6, flex: '1 1 380px' }}>
              <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Made of</Typography>
              {ecoBars(rows).map((b) => <Bar key={b.id} label={b.label} n={b.n} total={rows.length} color="#60a5fa" />)}
            </Box>
          </Stack>

          <Box sx={{ ...panel, overflow: 'hidden' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ p: 2.2 }}>
              <Typography sx={{ fontWeight: 700 }}>{bad.length ? 'Everything else' : 'All packages'}</Typography>
              <TextField size="small" placeholder="Search a package or CVE" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 240 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            </Stack>
            {!rest.length && <Typography color="text.secondary" sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>{q ? 'Nothing matches that.' : 'Everything has a known flaw. See the list above.'}</Typography>}
            {rest.slice(0, shown).map((p) => (
              <Stack key={p.id} direction="row" alignItems="center" gap={2} flexWrap="wrap" sx={{ px: 2.8, py: 1.2, borderTop: 1, borderColor: 'divider' }}>
                <Typography sx={{ flex: '2 1 220px', fontFamily: MONO, fontSize: 13.5, fontWeight: 600 }}>{p.name} <span style={{ opacity: 0.6, fontWeight: 400 }}>{p.version}</span></Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 90px' }}>{p.ecosystem}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 110px' }}>{p.license || 'No licence listed'}</Typography>
                <Pill color={GREEN}>{p.last_matched_at ? 'CLEAN' : 'NOT CHECKED'}</Pill>
              </Stack>
            ))}
            {rest.length > shown && <Box sx={{ p: 2, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}><Button color="inherit" onClick={() => setShown(shown + SHOWN)}>Show more ({rest.length - shown} left)</Button></Box>}
          </Box>
        </>
      )}
      <Typography variant="body2" color="text.secondary">Import a CycloneDX file, or a list with one package per line like name==1.2.3. We then check each package against the public OSV and NVD databases.</Typography>
      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)}>{toast ? <Alert severity={toast.severity} variant="filled">{toast.message}</Alert> : undefined}</Snackbar>
    </Stack>
  );
}
